import { FC, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Icon } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";

import { getUrlForDownload } from "@/api/axios";
import { MemberDocument } from "@/api/types/Document";
import { ThemedButton } from "@/premium/themedComponents/ThemedButton";
import { ThemedH3 } from "@/premium/themedComponents/Headers";
import { Card } from "@/premium/themedComponents";
import { ANALYTIC_EVENTS, logEvent } from "@/utils/analytics";
import { EnrollmentContext } from "@/state";
import "./DocumentList.scss";

interface DocumentRowProps {
  readonly memberDoc: MemberDocument;
  readonly openDocument: (doc: MemberDocument) => void;
  readonly downloadDocument: (doc: MemberDocument) => void;
}

const isIphoneUsingSafari = (() => {
  const isIphone = /iPhone/i.test(navigator.userAgent);
  const isSafari = !(/CriOS/i.test(navigator.userAgent))
    && !(/FxiOS/i.test(navigator.userAgent))
    && /Safari/i.test(navigator.userAgent);

  return isIphone && isSafari;
})();

interface DocumentListProps {
  readonly documents: MemberDocument[];
  readonly enrollmentPlanId: number;
}

export const DocumentList: FC<DocumentListProps> = ({ documents, enrollmentPlanId }) => {
  const { t: translate } = useTranslation();
  const [state] = useContext(EnrollmentContext);
  const docNameStyle = { fontFamily: (state.experienceData?.theme as { font?: string } | undefined)?.font || "Source Sans 3" };

  const MobileDocumentRow: FC<DocumentRowProps> = ({ memberDoc, openDocument }) => {
    return (
      <Grid className="document-grid">
        <Stack spacing="none">
          <p className="document-name" style={docNameStyle}>{memberDoc.name}</p>
          <p className="document-body-text">{memberDoc.description}</p>
          <Stack.Spacer />
          <Shelf>
            <ThemedButton appearance="text" onClick={() => openDocument(memberDoc)}>
              <Shelf>
                <Icon className="document-themed-icon" name="link-out" size="regular" />
                {translate("PLAN.documentList.openInNewTap")}
              </Shelf>
            </ThemedButton>
            <Shelf.Spacer />
          </Shelf>
        </Stack>
      </Grid>
    );
  };

  const TabletDocumentRow: FC<DocumentRowProps> = ({ memberDoc, openDocument, downloadDocument }) => {
    return (
      <Grid className="document-grid">
        <Grid.Span columns={6}>
          <Shelf>
            <Icon name="document" />

            <Stack spacing="none">
              <p className="document-name" style={docNameStyle}>{memberDoc.name}</p>
              <p className="document-body-text">{memberDoc.description}</p>
            </Stack>
            <Shelf.Spacer />
          </Shelf>
        </Grid.Span>

        <Grid.Span columns={6}>
          <Shelf>
            <Shelf.Spacer />
            <Stack>
              <ThemedButton appearance="text" onClick={() => openDocument(memberDoc)}>
                <Shelf>
                  <Icon className="document-themed-icon" name="link-out" size="regular" />
                  {translate("PLAN.documentList.openInNewTap")}
                </Shelf>
              </ThemedButton>

              <ThemedButton appearance="text" onClick={() => downloadDocument(memberDoc)}>
                <Shelf>
                  <Icon className="document-themed-icon" name="download" size="regular" />
                  {translate("PLAN.documentList.downloadPdf")}
                </Shelf>
              </ThemedButton>
            </Stack>
          </Shelf>
        </Grid.Span>
      </Grid>
    );
  };

  const DesktopDocumentRow: FC<DocumentRowProps> = ({ memberDoc, openDocument, downloadDocument }) => {
    return (
      <Grid className="document-grid">
        <Grid.Span columns={6}>
          <Shelf>
            <Icon name="document" />
            <Stack spacing="none">
              <p className="document-name" style={docNameStyle}>{memberDoc.name}</p>
              <p className="document-body-text">{memberDoc.description}</p>
            </Stack>
            <Shelf.Spacer />
          </Shelf>
        </Grid.Span>

        <Grid.Span columns={6}>
          <Shelf>
            <Shelf.Spacer />
            <ThemedButton appearance="text" onClick={() => openDocument(memberDoc)}>
              <Shelf>
                <Icon className="document-themed-icon" name="link-out" size="regular" />
                {translate("PLAN.documentList.openInNewTap")}
              </Shelf>
            </ThemedButton>

            <ThemedButton appearance="text" onClick={() => downloadDocument(memberDoc)}>
              <Shelf>
                <Icon className="document-themed-icon" name="download" size="regular" />
                {translate("PLAN.documentList.downloadPdf")}
              </Shelf>
            </ThemedButton>
          </Shelf>
        </Grid.Span>
      </Grid>
    );
  };

  const { displayRules } = useResponsive(
    useMemo(() => {
      return {
        displayRules: {
          desktop: {
            documentRowComponent: DesktopDocumentRow,
          },
          tablet: {
            documentRowComponent: TabletDocumentRow,
          },
          mobile: {
            documentRowComponent: MobileDocumentRow,
          },
        },
      };
    }, []),
  );

  const openDoc = useCallback((url: string) => {
    if (isIphoneUsingSafari) {
      window.open(url, "_parent");
    } else {
      window.open(url, "_blank", "noreferrer");
    }
  }, []);

  const openDocument = useCallback((doc: MemberDocument) => {
    logEvent(ANALYTIC_EVENTS.PLAN_DOCUMENT.new_tab);
    getUrlForDownload(doc.id, enrollmentPlanId, "view").then((response) => {
      openDoc(response.url);
    });
  }, [openDoc, enrollmentPlanId]);

  const downloadDocument = useCallback((doc: MemberDocument) => {
    logEvent(ANALYTIC_EVENTS.PLAN_DOCUMENT.download);
    getUrlForDownload(doc.id, enrollmentPlanId, "download").then((response) => {
      openDoc(response.url);
    });
  }, [openDoc, enrollmentPlanId]);

  const hasDocs = documents.length > 0;

  return (
    <>
      <Card>
        <ThemedH3>{translate("PLAN.documentList.planDocuments")}</ThemedH3>
        {hasDocs && <p className="document-body-text">{translate("PLAN.documentList.findInformationAboutYourPlan")}</p>}
        {!hasDocs && <p className="document-body-text">{translate("PLAN.documentList.thereAreNoDocuments")}</p>}
        {hasDocs && (
          <Stack>
            {documents.map((memberDoc) => {
              return (
                <displayRules.documentRowComponent
                  key={memberDoc.id}
                  memberDoc={memberDoc}
                  openDocument={openDocument}
                  downloadDocument={downloadDocument}
                />
              );
            })}
          </Stack>
        )}
      </Card>
    </>
  );
};
